import { ChoiceList, Spinner, Text } from "@shopify/polaris"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import dashboard from "@/api/dashboard"
import ShopifyGraphql from "@/api/shopify-graphql/shopify-graphql"
import { Menu, MenuItem } from "@/api/shopify-graphql/shopify-graphql.type"
import MultiPopup from "@/component/MultiPopup"
import { DashboardSetupGuideEnum } from "@/enums/dashboard"
import stores from "@/stores"
import toast from "@/utils/toast"

import { SortableTree } from "../MenuTree/SortableTree"
import type { TreeItem, TreeItems } from "../MenuTree/types"

interface AddTrackingPageModalProps {
  show: boolean
  trackURL: string
  onClose: () => void
  onSuccess?: () => void | Promise<void>
}

const AddTrackingPageModal = ({
  show,
  onClose,
  trackURL,
  onSuccess,
}: AddTrackingPageModalProps) => {
  const { t } = useTranslation(["dashboard", "common"])
  const [multiPopupIndex, setMultiPopupIndex] = useState(0)
  const [menus, setMenus] = useState<Menu[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [selectedMenu, setSelectedMenu] = useState<string>("")
  const [menuItems, setMenuItems] = useState<MenuItem[]>([])
  const [originalMenuItemsMap, setOriginalMenuItemsMap] = useState<Map<string, MenuItem>>(new Map())
  const sortableTreeRef = useRef<HTMLDivElement>(null)
  const [trackOrderText, setTrackOrderText] = useState("Track Order")

  useEffect(() => {
    try {
      dashboard.getMenuConfig().then(({ data }) => {
        setTrackOrderText(data.shopify_menu_config.translations.track_your_order)
      })
    } catch (error) {
      console.log("get shopify_menu_config error")
    }
  }, [])

  // 生成唯一的tracking page ID
  const generateTrackingPageId = () => `tracking-page-${Date.now()}-${Math.random().toString(36)
    .substr(2, 9)}`

  // 判断是否为tracking page
  const isTrackingPage = (id: string) => id.startsWith("tracking-page-")

  // 递归创建菜单项映射，保留原始类型信息
  const createMenuItemsMap = (items: MenuItem[], map: Map<string, MenuItem> = new Map()): Map<string, MenuItem> => {
    items.forEach(item => {
      map.set(item.id, item)
      if (item.items && item.items.length > 0) {
        createMenuItemsMap(item.items, map)
      }
    })
    return map
  }

  // 检查某个菜单项的后代是否包含特殊项
  const hasSpecialInDescendants = (items: MenuItem[]): boolean => {
    return items.some(item => {
      // 如果当前项是特殊项，返回true
      if (isTrackingPage(item.id)) {
        return true
      }
      // 递归检查子项
      if (item.items && item.items.length > 0) {
        return hasSpecialInDescendants(item.items)
      }
      return false
    })
  }

  // 将 MenuItem[] 转换为 TreeItems 格式
  const convertToTreeItems = (items: MenuItem[]): TreeItems => {
    return items.map((item): TreeItem => ({
      id: item.id,
      title: item.title,
      children: item.items ? convertToTreeItems(item.items) : [],
      collapsed: item.items && item.items.length > 0 ? !hasSpecialInDescendants(item.items) : false, // 如果后代中没有特殊项则折叠
      isSpecial: isTrackingPage(item.id),
      url: item.url || "",
    }))
  }

  // 将 TreeItems 转换回 MenuItem[] 格式，保留原始类型信息
  const convertToMenuItems = (items: TreeItems): MenuItem[] => {
    return items.map((item): MenuItem => {
      const originalItem = originalMenuItemsMap.get(item.id as string)
      const isNewTracking = isTrackingPage(item.id as string)

      return {
        id: item.id as string,
        title: item.title,
        type: isNewTracking ? "HTTP" : (originalItem?.type || "HTTP"), // 保留原始类型，新tracking page使用HTTP
        resourceId: originalItem?.resourceId || null,
        url: item.url || (isNewTracking ? trackURL : null),
        items: item.children ? convertToMenuItems(item.children) : [],
        tags: originalItem?.tags || [], // 保留原始标签
      }
    })
  }

  // 处理拖拽更新
  const handleTreeUpdate = (updatedItems: TreeItems) => {
    // 创建一个当前menuItems的title映射，保留用户手动编辑的内容
    const currentTitleMap = new Map<string, string>()
    const flattenedCurrentItems = flattenMenuItems(menuItems)
    flattenedCurrentItems.forEach(item => {
      currentTitleMap.set(item.id, item.title)
    })

    // 更新树结构时保留用户编辑的title
    const preserveEditedTitles = (items: TreeItems): TreeItems => {
      return items.map(item => ({
        ...item,
        title: currentTitleMap.get(item.id as string) ?? item.title,
        children: item.children ? preserveEditedTitles(item.children) : []
      }))
    }

    const updatedItemsWithPreservedTitles = preserveEditedTitles(updatedItems)
    const newMenuItems = convertToMenuItems(updatedItemsWithPreservedTitles)

    setMenuItems(newMenuItems)

    // 更新 menus 中的对应菜单
    setMenus(prevMenus => {
      return prevMenus.map(menu => {
        if (menu.id === selectedMenu) {
          return {
            ...menu,
            items: newMenuItems,
          }
        }
        return menu
      })
    })
  }

  // 处理标签更新
  const handleLabelChange = (itemId: string, label: string) => {
    setMenuItems(prevItems => {
      const updateItem = (items: MenuItem[]): MenuItem[] => {
        return items.map(item => {
          if (item.id === itemId) {
            return { ...item, title: label }
          }
          if (item.items && item.items.length > 0) {
            return { ...item, items: updateItem(item.items) }
          }
          return item
        })
      }

      return updateItem(prevItems)
    })
  }

  // 处理链接更新
  const handleUrlChange = (itemId: string, url: string) => {
    setMenuItems(prevItems => {
      const updateItem = (items: MenuItem[]): MenuItem[] => {
        return items.map(item => {
          if (item.id === itemId) {
            return { ...item, url }
          }
          if (item.items && item.items.length > 0) {
            return { ...item, items: updateItem(item.items) }
          }
          return item
        })
      }

      return updateItem(prevItems)
    })
  }

  // 检查某个菜单项是否可以拖拽
  const isItemDraggable = (itemId: string): boolean => {
    return isTrackingPage(itemId)
  }

  // 检查是否有特殊项的label为空
  const hasEmptySpecialItemLabel = (): boolean => {
    const flattenedItems = flattenMenuItems(menuItems)

    return flattenedItems.some(item => isTrackingPage(item.id) && (!item.title || item.title.trim() === ""))
  }

  // 扁平化菜单项，递归遍历所有层级
  const flattenMenuItems = (items: MenuItem[]): MenuItem[] => {
    const flattened: MenuItem[] = []

    const traverse = (items: MenuItem[]) => {
      items.forEach(item => {
        flattened.push(item)
        if (item.items && item.items.length > 0) {
          traverse(item.items)
        }
      })
    }

    traverse(items)
    return flattened
  }

  // 为菜单项添加跟踪页面
  const addTrackingPageToMenu = (items: MenuItem[]): MenuItem[] => {
    // 扁平化所有菜单项进行检查
    const flattenedItems = flattenMenuItems(items)
    const hasTrackingPage = flattenedItems.some(item => isTrackingPage(item.id))

    // 如果已存在tracking page，则不添加
    if (hasTrackingPage) {
      return items
    }

    // 添加新的tracking page
    return [
      ...items,
      {
        id: generateTrackingPageId(),
        title: trackOrderText,
        type: "HTTP", // 使用 HTTP 类型
        resourceId: null,
        url: trackURL, // 使用相对路径
        items: [],
        tags: [],
      },
    ]
  }

  // 滚动到特殊项位置
  const scrollToSpecialItem = () => {
    if (!sortableTreeRef.current) return

    // 延迟执行以确保DOM已渲染完成，增加延迟时间确保SortableTree完全渲染
    setTimeout(() => {
      const specialElement = sortableTreeRef.current?.querySelector("[data-special=\"true\"]")

      if (specialElement) {
        specialElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        })
      }
    }, 300)
  }

  /**
   * 数据请求
   * 获取menus列表
   */
  useEffect(() => {
    if (show) {
      fetchMenus()
    }
  }, [show])

  // 监听menuItems变化，自动滚动到特殊项
  useEffect(() => {
    if (menuItems.length > 0 && multiPopupIndex === 1) {
      scrollToSpecialItem()
    }
  }, [menuItems, multiPopupIndex])

  const fetchMenus = async () => {
    setLoading(true)
    try {
      const response = await ShopifyGraphql.getMenu()

      if (response.status === 200 && response.data) {
        // 过滤主菜单
        const menus = response.data.menus.nodes.filter(menu => menu.handle !== "customer-account-main-menu")

        setMenus(menus)
        // 默认选择第一个菜单
        if (menus.length > 0) {
          // setSelectedMenu(menus[0].id)
        }
      } else if (response.errors && response.errors[0]){
        toast(response.errors[0].message,  2e3, true)
      }
    } catch (error) {
      console.error("Get menus error:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    setMultiPopupIndex(0)
    onClose()
  }

  const handleSave = async () => {
    if (!selectedMenu || menuItems.length === 0) {
      return
    }

    setSaving(true)
    try {
      // 找到当前选中的菜单获取标题
      const currentMenu = menus.find(menu => menu.id === selectedMenu)

      if (!currentMenu) {
        console.error("current menu is not found")
        return
      }

      // 将 MenuItem[] 转换为 MenuItemUpdateInput[] 格式
      const convertMenuItems = (items: MenuItem[]): any[] => {
        return items.map(item => {
          // 对于新创建的tracking page，不传递ID让Shopify自动生成
          const isNewTrackingPage = isTrackingPage(item.id)

          return {
            ...(isNewTrackingPage ? {} : { id: item.id }), // 新的tracking page不传ID
            title: item.title,
            url: item.url,
            type: item.type, // 类型已经在convertToMenuItems中正确处理了
            resourceId: item.resourceId,
            tags: item.tags || [],
            items: item.items ? convertMenuItems(item.items) : [],
          }
        })
      }

      const menuItemsInput = convertMenuItems(menuItems)

      // 调用 Shopify GraphQL API 更新菜单
      const response = await ShopifyGraphql.setMenu(
        selectedMenu,
        currentMenu.title,
        menuItemsInput,
      )

      if (response.status === 200) {
        console.log("菜单更新成功:", response.data)

        // 先检查任务是否已完成，只有未完成的任务才需要检查和更新
        const { quickSetupData } = stores.userInfoSore
        const targetTask = quickSetupData?.task?.find(task => task.id === DashboardSetupGuideEnum.ADD_TRACKING_PAGE)
        const isTaskCompleted = targetTask?.is_completed || false

        if (!isTaskCompleted) {
          // 检查是否增加了 TrackingPage 项
          const flattenedItems = flattenMenuItems(menuItems)
          const hasAddedTrackingPage = flattenedItems.some(item => isTrackingPage(item.id))

          if (hasAddedTrackingPage) {
            // 直接在组件内部更新任务状态
            try {
              await dashboard.postMenuAdded()

              // 更新 store 中的 quickSetupData 状态
              if (quickSetupData && targetTask) {
                const updatedQuickSetupData = { ...quickSetupData }
                const updatedTask = updatedQuickSetupData.task.find(task => task.id === DashboardSetupGuideEnum.ADD_TRACKING_PAGE)

                if (updatedTask) {
                  updatedTask.is_completed = true
                  stores.userInfoSore.setQuickSetupData(JSON.parse(JSON.stringify(updatedQuickSetupData)))
                }
              }
            } catch (error) {
              console.error("更新任务状态失败:", error)
              // 不影响主要功能，只记录错误
            }
          }
        } else {
          console.log("ADD_TRACKING_PAGE 任务已完成，无需重复更新")
        }

        await onSuccess?.()

        toast(t("common:SavedSuccess"))
        setMultiPopupIndex(0)
        onClose()
      } else {
        toast(t("common:SavedFailed"))
        console.error("菜单更新失败:", response)
      }
    } catch (error) {
      toast(t("common:SavedFailed"))
      console.error("保存菜单时发生错误:", error)
    } finally {
      setSaving(false)
    }
  }

  // 生成菜单选项
  const menuChoices = menus.map(menu => ({
    label: menu.title,
    value: menu.id,
  }))

  return (
    <div>
      <MultiPopup
        active={multiPopupIndex}
        open={show}
        onClose={handleCancel}
        preventCloseOnClickOutside
        onChange={(index) => setMultiPopupIndex(index)}
        stackData={[
          {
            title: t("dashboard:AddTrackingPage"),
            children: <div className="flex flex-col gap-2">
              {loading && menuChoices.length === 0
                ? <div className={"min-h-40 flex justify-center items-center"}>
                  <Spinner accessibilityLabel="Spinner example" size="large" />
                </div>
                : <>
                  <Text as={"p"} fontWeight={"medium"}>{t("dashboard:ChooseALiveMenu")}</Text>
                  <ChoiceList
                    title=""
                    titleHidden
                    choices={menuChoices}
                    selected={[selectedMenu]}
                    onChange={([id]) => setSelectedMenu(id)}
                  />
                </>
              }

            </div>,
            primaryAction: {
              content: t("common:NextStep"),
              disabled: !selectedMenu,
              onAction: () => {
                const activeMenu = menus.find(menu => menu.id === selectedMenu)

                if (activeMenu) {
                  // 创建原始菜单项映射，保留类型信息
                  const originalMap = createMenuItemsMap(activeMenu.items)

                  setOriginalMenuItemsMap(originalMap)

                  // 当进入第二步时，在菜单项末尾添加跟踪页面
                  const updatedItems = addTrackingPageToMenu(activeMenu.items)

                  setMenuItems(updatedItems)
                }

                setMultiPopupIndex(1)
              },
            },
          },
          {
            title: t("dashboard:AddMenuItem"),
            children: <div>
              <Text as={"p"} fontWeight={"medium"}>{t("dashboard:FillTheLabelField")}</Text>
              <div className={"mt-3 mb-[-4px]"} ref={sortableTreeRef}>
                {!!menuItems.length &&
                  <SortableTree
                    defaultItems={convertToTreeItems(menuItems)}
                    collapsible
                    indicator
                    removable
                    onItemsChange={handleTreeUpdate}
                    disableDrag={false}
                    isItemDraggable={isItemDraggable}
                    onItemLabelChange={handleLabelChange}
                    onItemUrlChange={handleUrlChange}
                    trackOrderPlaceholder={trackOrderText}
                  />}
              </div>
            </div>,
            primaryAction: {
              content: t("common:Save"),
              loading: saving,
              disabled: saving || hasEmptySpecialItemLabel(),
              onAction: handleSave,
            },
          },
        ]}
      />
    </div>
  )
}

export default AddTrackingPageModal
